/**
 * Who is working the queue, for the alert audit trail.
 *
 * WHY THIS IS NOT AUTHENTICATION, and why that is fine here. The console sits on an
 * internal network behind the operation's own access control; this is not a login and
 * nothing is authorised by it. It answers one question the audit could not answer before:
 * every alert action was recorded as `by_whom = 'supervisor'`, a constant, so the trail
 * could say an alert was attended and never who attended it. A shift handover, a dispute
 * about whether a driver was actually called, or a before/after intervention study all need
 * the name.
 *
 * It is SELF-DECLARED and the UI says so. A self-declared name is weak evidence on its own
 * and strong evidence next to the call centre's own records, which is exactly how it is
 * meant to be used -- the point of recording `called` separately from `attended` is that
 * the second one can be checked against a phone log later.
 *
 * Stored per browser rather than per session so a supervisor names themselves once at the
 * start of a shift and not once per alert.
 */

const KEY = 'fleet.supervisor'

export function getSupervisor(): string | null {
  try {
    const v = window.localStorage.getItem(KEY)
    return v && v.trim() ? v.trim() : null
  } catch {
    // Private browsing, or storage disabled. Not a reason to block triage.
    return null
  }
}

export function setSupervisor(name: string): void {
  try {
    const v = name.trim()
    if (v) window.localStorage.setItem(KEY, v)
    else window.localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

/**
 * The name to record, asking once if it is not yet known.
 *
 * Returns `null` only if the operator actively declines. A triage action is NOT blocked in
 * that case -- an alert attended anonymously is better than an alert nobody touched because
 * a dialog was in the way, and `by_whom` then falls back to the old constant so the row
 * still exists and is visibly unattributed.
 */
export function requireSupervisor(prompt: (msg: string, dflt: string) => string | null): string {
  const known = getSupervisor()
  if (known) return known
  const entered = prompt(
    'Your name, for the alert audit trail. Recorded against everything you attend or call.',
    '',
  )
  if (entered && entered.trim()) {
    setSupervisor(entered)
    return entered.trim()
  }
  return 'unattributed'
}
